import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/apiService';
import './InsuranceQuoteForm.css';

interface FormData {
  age: string;
  years_driving: string;
  car_type: string;
  car_year: string;
  zip_code: string;
}

interface QuoteResult {
  annual_premium: number;
  monthly_premium: number;
  user_data: FormData;
  message: string;
}

const InsuranceQuoteForm: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    age: '',
    years_driving: '',
    car_type: '',
    car_year: '',
    zip_code: ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [quoteResult, setQuoteResult] = useState<QuoteResult | null>(null);
  const [apiError, setApiError] = useState<string>('');

  const carTypes = [
    { value: '', label: 'Select car type' },
    { value: 'sedan', label: 'Sedan' },
    { value: 'suv', label: 'SUV' },
    { value: 'truck', label: 'Truck' },
    { value: 'sports', label: 'Sports Car' },
    { value: 'luxury', label: 'Luxury Car' },
    { value: 'compact', label: 'Compact Car' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    // Age validation
    const age = parseInt(formData.age);
    if (!formData.age) {
      newErrors.age = 'Age is required';
    } else if (isNaN(age) || age < 18 || age > 80) {
      newErrors.age = 'Age must be between 18 and 80';
    }

    // Years driving validation
    const yearsDriving = parseInt(formData.years_driving);
    if (!formData.years_driving) {
      newErrors.years_driving = 'Years of driving experience is required';
    } else if (isNaN(yearsDriving) || yearsDriving < 0 || yearsDriving > 60) {
      newErrors.years_driving = 'Years driving must be between 0 and 60';
    } else if (yearsDriving > age - 16) {
      newErrors.years_driving = 'Years driving cannot exceed age minus 16';
    }

    // Car type validation
    if (!formData.car_type) {
      newErrors.car_type = 'Car type is required';
    }

    // Car year validation
    const carYear = parseInt(formData.car_year);
    const currentYear = new Date().getFullYear();
    if (!formData.car_year) {
      newErrors.car_year = 'Car year is required';
    } else if (isNaN(carYear) || carYear < 1990 || carYear > currentYear) {
      newErrors.car_year = `Car year must be between 1990 and ${currentYear}`;
    }

    // Zip code validation
    if (!formData.zip_code) {
      newErrors.zip_code = 'Zip code is required';
    } else if (!/^\d{5}$/.test(formData.zip_code)) {
      newErrors.zip_code = 'Zip code must be 5 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError('');
    setQuoteResult(null);

    try {
      // Call Lambda API directly
      const requestData = {
        age: parseInt(formData.age),
        years_driving: parseInt(formData.years_driving),
        car_type: formData.car_type,
        car_year: parseInt(formData.car_year),
        zip_code: parseInt(formData.zip_code)
      };

      console.log('Requesting quote from Lambda API...');
      console.log('Request data:', requestData);

      const response = await fetch('https://wwqceneduk.execute-api.us-east-1.amazonaws.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to get quote: ${errorText}`);
      }

      const result: QuoteResult = await response.json();
      console.log('Quote result:', result);
      setQuoteResult(result);
    } catch (error) {
      console.error('Error creating quote:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        setApiError('Network error: Unable to connect to the quote service. Please check your internet connection.');
      } else {
        setApiError(error instanceof Error ? error.message : 'Failed to create quote. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to estimate vehicle value based on type and year
  const estimateVehicleValue = (carType: string, year: number): number => {
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;

    let baseValue = 25000; // Default base value

    switch (carType) {
      case 'luxury': baseValue = 60000; break;
      case 'sports': baseValue = 45000; break;
      case 'suv': baseValue = 35000; break;
      case 'truck': baseValue = 30000; break;
      case 'sedan': baseValue = 25000; break;
      case 'compact': baseValue = 20000; break;
    }

    // Depreciate by 15% per year
    const depreciatedValue = baseValue * Math.pow(0.85, age);
    return Math.max(depreciatedValue, 5000); // Minimum value of $5000
  };

  const handleReset = () => {
    setFormData({
      age: '',
      years_driving: '',
      car_type: '',
      car_year: '',
      zip_code: ''
    });
    setErrors({});
    setQuoteResult(null);
    setApiError('');
  };

  return (
    <div className="quote-form-container">
      <div className="quote-form-header">
        <h1>Get Your Insurance Quote</h1>
        <p>Answer a few simple questions to get an instant insurance quote</p>
      </div>

      <div className="quote-form-content">
        <form onSubmit={handleSubmit} className="insurance-quote-form">
          <div className="form-section">
            <h2>Personal Information</h2>

            <div className="form-group">
              <label htmlFor="age">Age*</label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                min="18"
                max="80"
                placeholder="Enter your age"
                className={errors.age ? 'error' : ''}
              />
              {errors.age && <span className="error-message">{errors.age}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="years_driving">Years of Driving Experience*</label>
              <input
                type="number"
                id="years_driving"
                name="years_driving"
                value={formData.years_driving}
                onChange={handleInputChange}
                min="0"
                max="60"
                placeholder="How many years have you been driving?"
                className={errors.years_driving ? 'error' : ''}
              />
              {errors.years_driving && <span className="error-message">{errors.years_driving}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="zip_code">Zip Code*</label>
              <input
                type="text"
                id="zip_code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleInputChange}
                placeholder="12345"
                maxLength={5}
                className={errors.zip_code ? 'error' : ''}
              />
              {errors.zip_code && <span className="error-message">{errors.zip_code}</span>}
            </div>
          </div>

          <div className="form-section">
            <h2>Vehicle Information</h2>

            <div className="form-group">
              <label htmlFor="car_type">Car Type*</label>
              <select
                id="car_type"
                name="car_type"
                value={formData.car_type}
                onChange={handleInputChange}
                className={errors.car_type ? 'error' : ''}
              >
                {carTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.car_type && <span className="error-message">{errors.car_type}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="car_year">Car Year*</label>
              <input
                type="number"
                id="car_year"
                name="car_year"
                value={formData.car_year}
                onChange={handleInputChange}
                min="1990"
                max={new Date().getFullYear()}
                placeholder="2020"
                className={errors.car_year ? 'error' : ''}
              />
              {errors.car_year && <span className="error-message">{errors.car_year}</span>}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
              disabled={isLoading}
            >
              Reset Form
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Getting Quote...' : 'Get My Quote'}
            </button>
          </div>
        </form>

        {/* Error Display */}
        {apiError && (
          <div className="api-error">
            <div className="error-icon">⚠️</div>
            <div className="error-content">
              <h3>Unable to Get Quote</h3>
              <p>{apiError}</p>
              {apiError.includes('port 5003') && (
                <div className="error-help">
                  <p>To start the ML model server:</p>
                  <code>cd mlmodel && python main.py</code>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quote Results */}
        {quoteResult && (
          <div className="quote-results">
            <div className="quote-header">
              <div className="quote-icon">🎉</div>
              <h2>Your Insurance Quote</h2>
              <p>Based on the information you provided</p>
            </div>

            <div className="quote-pricing">
              <div className="price-card primary">
                <div className="price-label">Annual Premium</div>
                <div className="price-amount">${quoteResult.annual_premium.toFixed(2)}</div>
                <div className="price-period">per year</div>
              </div>

              <div className="price-card secondary">
                <div className="price-label">Monthly Premium</div>
                <div className="price-amount">${quoteResult.monthly_premium.toFixed(2)}</div>
                <div className="price-period">per month</div>
              </div>
            </div>

            <div className="quote-details">
              <h3>Quote Details</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Age:</span>
                  <span className="detail-value">{quoteResult.user_data.age} years old</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Driving Experience:</span>
                  <span className="detail-value">{quoteResult.user_data.years_driving} years</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle:</span>
                  <span className="detail-value">{quoteResult.user_data.car_year} {quoteResult.user_data.car_type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Location:</span>
                  <span className="detail-value">{quoteResult.user_data.zip_code}</span>
                </div>
              </div>
            </div>

            <div className="quote-actions">
              <button className="btn-primary" onClick={() => window.print()}>
                Print Quote
              </button>
              <button className="btn-secondary" onClick={handleReset}>
                Get Another Quote
              </button>
            </div>

            <div className="quote-disclaimer">
              <p><strong>Note:</strong> This is an estimated quote generated by our AI model. Final rates may vary based on additional factors and underwriting review.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsuranceQuoteForm;